import { Box } from '@chakra-ui/react';
import VFormFieldHorizontal from '@voly/ui-components/src/v2/VFormFieldHorizontal';
import VTooltip from '@voly/ui-components/src/v2/VTooltip';
import { ReactElement, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import { ECustomFieldType } from 'src/app/project/modules/custom-fields/custom-field-types-enums';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import { EDateFormat } from 'src/app/project/shared/enums/date-format.enum';
import PointFieldName from './PointFieldName';
import {
  parseDateRange,
  serialiseDate,
  serialiseDateRange,
  TDateRange,
} from '../../../utils/dateValue';
import DatePicker from '../DatePicker';
import DateRangePicker from '../DateRangePicker';

interface PointFieldDateProps {
  field: TCustomField;
  value: string;
  readOnly: boolean;
  dateFormat: EDateFormat;
  onSave: (value: string) => void;
}

const PointFieldDate = ({
  field,
  value,
  readOnly,
  dateFormat,
  onSave,
}: PointFieldDateProps): ReactElement => {
  const isTimeline = field.type === ECustomFieldType.TIMELINE;
  const savedRange = parseDateRange(value);
  const [draft, setDraft] = useState<TDateRange>(savedRange);
  const [savedValue, setSavedValue] = useState(value);
  const locked = field.lockedValue;

  if (savedValue !== value) {
    setSavedValue(value);
    setDraft(savedRange);
  }

  const commit = (next: TDateRange): void => {
    setDraft(next);
    const serialised = isTimeline ? serialiseDateRange(next) : serialiseDate(next.from);
    if (serialised === undefined || serialised === value) {
      return;
    }
    onSave(serialised);
  };

  const pickerProps = {
    size: 'sm' as const,
    dateFormat,
    readOnly,
    disabled: locked,
    locked,
  };

  return (
    <VFormFieldHorizontal
      data-testid="point-custom-field-row"
      size="sm"
      label={<PointFieldName field={field} />}
      helperText={field.description}
      errorText={undefined}
      invalid={undefined}
      readOnly={readOnly}
      disabled={locked}
    >
      <VTooltip content={translate('locked_by_admin')} disabled={!locked}>
        <Box data-testid="point-custom-field-control">
          {isTimeline ? (
            <DateRangePicker {...pickerProps} value={draft} onChange={commit} />
          ) : (
            <DatePicker
              {...pickerProps}
              values={[draft.from]}
              onValuesChange={([from]) => commit({ from, to: null })}
            />
          )}
        </Box>
      </VTooltip>
    </VFormFieldHorizontal>
  );
};

export default PointFieldDate;
